import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import Panel from 'utils/panel';
// NOTE POC for real time inventory in minicart
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { formatMoney, formatCartLinesAjax } from 'utils/storefront/format-resource';
import CloseIcon from 'utils/icon/icon-close.svg';
import ShopifyPayIcon from 'utils/icon/icon-shopify-pay.svg';
import GooglePayIcon from 'utils/icon/icon-google-pay.svg';
import PayPalIcon from 'utils/icon/icon-paypal.svg';
import TrapFocus from 'utils/trap-focus';
import { decodeId } from 'utils/helpers';
import CartItem from './cart-item';
import { withCartContext } from './cart-context';

const Cart = (props) => {
  const { cart, cartError } = props;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get('cart');
  const [isOpen, setIsOpen] = useState(isCartRedirect);
  const [productsData, setProductsData] = useState({});
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });

  useEffect(() => {
    if (!closeTriggerEl.current) return;

    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  let trapFocus = null;

  useEffect(() => {
    if (isOpen) {
      trapFocus = new TrapFocus(document.querySelector('[data-minicart]'));
      document.body.classList.add('scroll-lock');
      trapFocus.init();
    }

    return () => {
      document.body.classList.remove('scroll-lock');
      if (trapFocus) trapFocus.destroy(document.querySelector('[data-header-cart-trigger]'));
    };
  }, [isOpen, trapFocus]);

  const lines = cart?.mode === 'storefront' ? cart?.state?.lines : formatCartLinesAjax(cart?.state?.items);

  const storefront = new Storefront();
  const getProductsData = (cartItems) =>
    storefront.request({
      query: queries.variantsById,
      variables: {
        ids: cartItems.map((item) => `gid://shopify/ProductVariant/${item.variant_id}`),
      },
    });

  useEffect(() => {
    if (lines.length) {
      getProductsData(lines).then((response) => {
        if (response?.data) {
          setProductsData(
            Object.fromEntries(response?.data?.nodes?.map((item) => [decodeId(item.id, 'ProductVariant'), item])),
          );
        }
      });
    } else {
      setProductsData({});
    }
  }, [cart]);

  const EmptyCartContent = () => (
    <div className="flex flex-col items-center gap-16 px-20 py-80 lg:px-80">
      <h3 className="h1 m-0">{window.TMW.Strings.cart.empty}</h3>
      {props.data.empty_button_link && props.data.empty_button_text && (
        <a
          className="button button--secondary no-underline"
          href={props.data.empty_button_link}
        >
          {props.data.empty_button_text}
        </a>
      )}
    </div>
  );

  const FreeShippingBanner = () => (
    <div className="px-10 py-16 sm:px-24">
      <div className="rounded-lg bg-zulily-lightPurple  py-10 text-center tracking-xs sm:mx-24 ">
        {props.data.free_shipping_message}
      </div>
    </div>
  );

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      showBackdrop={true}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      context="cart"
      additionalClasses="w-full max-w-[480px]"
    >
      <div
        className="relative flex h-full flex-col"
        data-minicart
      >
        <button
          type="button"
          ref={closeTriggerEl}
          aria-label={window.TMW.Strings.cart.close}
          className="absolute right-22 top-32"
        >
          <CloseIcon />
        </button>
        <div className="flex items-center gap-8 border-b border-gray-2 p-24">
          <span className="h2 m-0">{window.TMW.Strings.cart.cart}</span>
          <span className="font-belltownBold text-16"> ({cart.state.item_count})</span>
        </div>
        {!!cartError && (
          <div className="mx-24 mt-16 rounded-lg bg-zulily-lightPurple px-24 py-4 text-center text-error">
            {cartError}
          </div>
        )}
        {props.data.show_free_shipping_message &&
          cart.state.total_price / 100 >= props.data.free_shipping_threshold && <FreeShippingBanner />}
        {cart.state.item_count === 0 ? (
          <EmptyCartContent />
        ) : (
          <div className="scrollbar-hide flex h-auto grow flex-col gap-32 overflow-y-auto p-20 lg:gap-24 lg:p-24">
            {lines.map((line) => (
              <CartItem
                key={line.id}
                line={line}
                productData={productsData[line.variant_id]}
              />
            ))}
          </div>
        )}
        {cart.state.item_count > 0 && (
          <div className="border-t border-gray-2 p-24">
            <div className="mb-10 flex items-center justify-between">
              <h4 className="m-0">{window.TMW.Strings.cart.subtotal}</h4>
              <span className="font-belltownBold text-16">
                {cart.mode === 'storefront'
                  ? formatMoney(cart?.state?.subtotal || { amount: 0 })
                  : formatMoney(cart?.state?.total_price ? { amount: cart.state.total_price } : { amount: 0 }, true)}
              </span>
            </div>
            <a
              className="button button--primary h-44 w-full justify-center no-underline"
              href="/checkout"
            >
              {cart.mode === 'storefront' ? cart?.state?.checkoutUrl : window.TMW.Strings.cart.checkout}
            </a>
            {props.data.payment_buttons && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-12">
                {props.data.payment_text && (
                  <span className="font-belltownMedium tracking-xs">{props.data.payment_text}</span>
                )}
                <ul
                  className="m-0 flex list-none flex-wrap justify-center gap-16 gap-y-8 p-0"
                  role="list"
                >
                  {props.data.payment_buttons.map((button) => {
                    switch (button) {
                      case 'google_pay':
                        return (
                          <li
                            className="p-0"
                            aria-label={button}
                          >
                            <GooglePayIcon />
                          </li>
                        );
                      case 'shopify_pay':
                        return (
                          <li
                            className="p-0"
                            aria-label={button}
                          >
                            <ShopifyPayIcon />
                          </li>
                        );
                      case 'paypal':
                        return (
                          <li
                            className="p-0"
                            aria-label={button}
                          >
                            <PayPalIcon />
                          </li>
                        );
                      default:
                        return null;
                    }
                  })}{' '}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
